<script setup>
import { computed, inject } from 'vue';
import CrmSummaryCard from '../components/CrmSummaryCard.vue';
import FunnelsOverviewCard from '../components/FunnelsOverviewCard.vue';
import PipelineValueCard from '../components/PipelineValueCard.vue';
import ConversionRatesCard from '../components/ConversionRatesCard.vue';
import DealsAnalyticsCard from '../components/DealsAnalyticsCard.vue';
import SalesForecastCard from '../components/SalesForecastCard.vue';
import CrmAgentsPerformanceCard from '../components/CrmAgentsPerformanceCard.vue';
import WonLostAnalysisCard from '../components/WonLostAnalysisCard.vue';
import ProductsAppointmentsCard from '../components/ProductsAppointmentsCard.vue';
import CrmActivitiesCard from '../components/CrmActivitiesCard.vue';
import CompaniesAnalyticsCard from '../components/CompaniesAnalyticsCard.vue';
import DailyDealsChart from '../components/DailyDealsChart.vue';

// Get data from parent (already fetched centrally with 5s refresh)
const { crmDetailed, isLoading } = inject('analyticsData');

// Use detailed data from parent
const crmData = computed(() => crmDetailed.value || {});

// Computed properties for data sections
const summary = computed(() => crmData.value?.summary || {});
const funnels = computed(() => crmData.value?.funnels || []);
const pipelineValue = computed(() => crmData.value?.pipeline_value || {});
const conversionRates = computed(() => crmData.value?.conversion_rates || []);
const deals = computed(() => crmData.value?.deals || {});
const forecasting = computed(() => crmData.value?.forecasting || {});
const agentsPerformance = computed(
  () => crmData.value?.agents_performance || []
);
const wonLostAnalysis = computed(() => crmData.value?.won_lost_analysis || {});
const products = computed(() => crmData.value?.products || {});
const appointments = computed(() => crmData.value?.appointments || {});
const activities = computed(() => crmData.value?.activities || {});
const notes = computed(() => crmData.value?.notes || {});
const companies = computed(() => crmData.value?.companies || {});
const dailyDeals = computed(() => crmData.value?.daily_deals || []);
</script>

<template>
  <div class="flex flex-col gap-6">
    <template v-if="true">
      <!-- CRM Summary -->
      <CrmSummaryCard :data="summary" :loading="isLoading" />

      <!-- Row 1: Funnels Overview and Pipeline Value -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <FunnelsOverviewCard :data="funnels" :loading="isLoading" />
        <PipelineValueCard :data="pipelineValue" :loading="isLoading" />
      </div>

      <!-- Row 2: Conversion Rates and Deals Analytics -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ConversionRatesCard :data="conversionRates" :loading="isLoading" />
        <DealsAnalyticsCard :data="deals" :loading="isLoading" />
      </div>

      <!-- Row 3: Daily Deals Chart (Full Width) -->
      <DailyDealsChart :data="dailyDeals" :loading="isLoading" />

      <!-- Row 4: Sales Forecast and Won/Lost Analysis -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SalesForecastCard :data="forecasting" :loading="isLoading" />
        <WonLostAnalysisCard :data="wonLostAnalysis" :loading="isLoading" />
      </div>

      <!-- Row 5: Agents Performance (Full Width) -->
      <CrmAgentsPerformanceCard
        :data="agentsPerformance"
        :loading="isLoading"
      />

      <!-- Row 6: Companies Analytics -->
      <CompaniesAnalyticsCard :data="companies" :loading="isLoading" />

      <!-- Row 7: Products, Appointments and Activities -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ProductsAppointmentsCard
          :products="products"
          :appointments="appointments"
          :loading="isLoading"
        />
        <CrmActivitiesCard
          :activities="activities"
          :notes="notes"
          :loading="isLoading"
        />
      </div>
    </template>
  </div>
</template>
